import React, { useEffect, useState, useMemo } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  InputAdornment,
  TextField,
  CircularProgress,
  Button,
} from "@mui/material";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../utils/dataTableShell";
import { Add, CloseRounded, Edit } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import { Delete, MoreVert } from "@mui/icons-material";

import { v4 as uuid } from "uuid";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import AddSiteDrawer from "./AddSiteDrawer";
import { timezoneLabel } from "../../utils/timezone";

type Props = {
  canEdit: boolean;
};

const SitesList = ({ canEdit }: Props) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);

  const [studyData, setStudyData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get("query") ?? "");
  const [searchId, setSearchId] = useState(uuid());
  const [deleteLoader, setDeleteLoader] = useState(false);

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        setLoading(true);
        let url = `/registry/sites?`;
        if (searchText) {
          url += `&query=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          siteName: item?.name || "-",
          abbreviation: item?.abbreviation || "-",
          country: item?.country?.label || "-",
          countryId: item?.countryId || "-",
          date: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("MM/dd/yyyy")
            : "-",
          timezone: item?.timezone,
          timezoneLabel: timezoneLabel(item?.timezone),
        }));

        setStudyData(newData || []);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchStudyData();
  }, [searchText, toggleLoader]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (searchText) {
      params.set("query", searchText);
    }

    setSearchParams(params, {
      replace: true,
    });
  }, [setSearchParams, searchText]);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val);
      }, 500),
    [setSearchText]
  );
  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
  };

  const handleDeleteSite = async () => {
    try {
      setDeleteLoader(true);
      const res: AxiosResponse = await http.delete(
        `/registry/sites/${selectedRow.id}`
      );
      toastMessage("success", res.data.message);
      setDeleteLoader(false);
      refreshPage();
    } catch (err) {
      setDeleteLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const closeDrawer = () => {
    setOpenDrawer(false);
    setSelectedRow(null);
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TextField
              key={searchId}
              placeholder="Search"
              sx={{ width: "30%" }}
              defaultValue={searchText}
              onChange={(e) => modifySearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch}>
                      <CloseRounded />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {canEdit && (
              <Box sx={{ ml: "auto" }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenDrawer(true)}
                >
                  New Location
                </Button>
              </Box>
            )}
          </Box>
        }
        footer={
          <DataTableFooter
            filteredCount={studyData.length}
            page={0}
            pageSize={pageSize}
            onPageChange={() => {}}
            loading={loading}
            hidePagination
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
        <TableHead>
          <TableRow>
            <StyledTableCell>Location Name</StyledTableCell>
            <StyledTableCell>Abbreviation</StyledTableCell>
            <StyledTableCell>Country</StyledTableCell>
            <StyledTableCell>Timezone</StyledTableCell>
            <StyledTableCell>Creation Date</StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        {!loading && studyData?.length > 0 && (
          <TableBody>
            {studyData?.map((row: any) => (
              <TableRow key={row?.id}>
                <StyledTableCell>{row?.siteName}</StyledTableCell>
                <StyledTableCell>{row?.abbreviation}</StyledTableCell>
                <StyledTableCell>{row?.country}</StyledTableCell>
                <StyledTableCell>{row?.timezone}</StyledTableCell>
                <StyledTableCell>{row?.date}</StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton onClick={(e) => handleMenuClick(e, row)}>
                    <MoreVert />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
        </Table>
        {!loading && studyData.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setOpenDrawer(true);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleDeleteSite();
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
          {deleteLoader && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>

      <Drawer open={openDrawer} onClose={closeDrawer} anchor={"right"}>
        <AddSiteDrawer
          closeDrawer={closeDrawer}
          refreshPage={refreshPage}
          data={selectedRow}
        />
      </Drawer>
    </Box>
  );
};

export default SitesList;
